import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";
import { isEmpty, sortBy, debounce, cloneDeep } from "lodash";
import { Prompt } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { DateTimePickerWithNoFormsy } from "../common/DateTimePicker";
import Badge from "../common/Badge";

import * as modalActions from "../../actions/modalActions";
import * as callActions from "../../actions/callActions";
import TasksServiceContainer from "./TasksServiceContainer";
import { DropdownSearchWithNoFormsy } from "../common/DropdownSearch";
import Modal from "../common/Modal";
import Alert from "../common/Alert";
import CustomNumberInput from "../common/NumberInput";
import CustomInput from "../common/Input";
import CustomTextarea from "../common/Textarea";
import Checkbox from "../common/Checkbox";
import RecurringCallForm from "./RecurringCallForm";
import ConfirmUpdateCallModal from "./ConfirmUpdateCallModal";
import TimesheetProgressBar from "./timesheet/TimesheetProgressBar";
import { customToast as toast, stripHtml } from "../../utils";
import { formatDate } from "../../helpers/date";
import "./call.scss";

export const getClassName = (priority) => {
  if (priority === "Low") {
    return "success";
  }
  if (priority === "Normal") {
    return "warning";
  }
  return "danger";
};

const INITIAL_STATE = {
  customer: {},
  project: {},
  stage: {},
  tasks: [],
  workplace: {},
  recurrence: null,
  assigned_to: {},
  focusedInput: null,
  hasChanges: false,
  blurTriggered: false,
  priority: {}
};
const CallForm = ({
  call,
  codes,
  customers,
  contracts,
  projects,
  users,
  recurrence,
  adding,
  auth,
  customer,
  project,
  onAddWithRecurring,
  onAdd,
  onSave,
  onSaveWithRecurring,
  recurring,
  actions,
  onCancel,
  services,
  editing,
  saving,
  readOnly,
  clearProgressBarOnUnmount
}) => {
  const [state, setState] = useState(INITIAL_STATE);
  const [blurTriggered, setBlurTriggered] = useState({});
  const [isFormInputsChanged, setIsFormInputsChanged] = useState(false);

  useEffect(() => {
    if (adding && !saving) {
      const assigned_to = auth.user;
      const stage =
        codes?.find(
          (code) => code.entity === "Call" && code.label === "Reserved"
        ) || {};
      const workplace =
        codes?.find(
          (code) => code.entity === "Call" && code.value === "OFFICE"
        ) || {};
      setState((prev) => ({
        ...prev,
        stage,
        workplace,
        assigned_to
      }));
    } else if (!adding) {
      const customerCopy = customers?.find(
        (cust) => cust._id === call.customer?._id
      );
      let callProject;
      if (call.project) {
        callProject = projects?.find((proj) => proj._id === call.project._id);
      }
      const assigned_to = call.assigned_to
        ? users?.find((user) => user._id === call.assigned_to)
        : null;

      const { call_type } = call;
      const workplace = codes?.find(
        (code) => code.entity === "Call" && code.value === "OFFICE"
      );
      const contract =
        callProject && callProject.contract
          ? contracts?.find((cont) => cont._id === callProject.contract._id)
          : {};
      const service = call.service || null;

      const { stage } = call;

      const priority = codes?.find((code) => code._id === call.priority);

      const defaultState = {
        customer: customerCopy,
        project: callProject,
        contract,
        assigned_to,
        call_type: call_type && call_type._id,
        workplace: call.workplace || workplace,
        stage: call.stage,
        recurrence,
        service,
        managerApproved: stage && stage.order >= 5,
        tasks: call.tasks,
        priority,
        arrival_date: call.arrival_date,
        internal_notes: call.internal_notes,
        fixed_time: call.fixed_time,
        off_hours: call.off_hours,
        billable_hrs: call.billable_hrs,
        actual_hrs: call.actual_hrs,
        billing_description: stripHtml(call.billing_description)
      };

      setState((prev) => ({ ...prev, ...defaultState }));
    }

    if (customer) {
      setState((prev) => ({ ...prev, customer }));
    }
    if (project) {
      const call_type = codes?.find(
        (code) =>
          code.entity === "Call" && code.value === project.call_type?.value
      );
      const updates = { project, call_type: call_type._id };
      if (adding && !call_type.is_billable) {
        updates.billable_hrs = 0;
      }
      setState((prev) => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codes, adding, call, recurrence]);

  const canSubmit = useMemo(
    () =>
      state.customer?._id &&
      state.project?._id &&
      state.service &&
      !state.tasks.some(
        (task) => !task.service_task || task.actual_duration === ""
      ) &&
      state.arrival_date &&
      state.internal_notes &&
      state.internal_notes.length <= 500 &&
      (!state.billing_description || state.billing_description.length <= 100),
    [state]
  );

  // ----- GENERATORS --------
  const projectOptions = useMemo(() => {
    let options = [];
    if (adding) {
      if (!isEmpty(state.customer)) {
        options = projects
          .filter(
            (proj) =>
              proj.customer &&
              proj.customer._id === state.customer._id &&
              proj.project_status.label === "In Process" &&
              proj.name !== "Personal Time"
          )
          .map((proj) => ({ value: proj._id, label: proj.name }));
      }
    } else if (!isEmpty(state.customer)) {
      options = projects
        .filter(
          (proj) =>
            proj.customer &&
            proj.customer._id === state.customer._id &&
            (proj.project_status.label === "In Process" ||
              proj.project_status.label === "Ready To Close")
        )
        .map((proj) => ({ value: proj._id, label: proj.name }));
    }

    return sortBy(options, [(option) => option.label.toLowerCase()]);
  }, [adding, state.customer, projects]);

  const stageOptions = useMemo(() => {
    const validStatuses = new Set(["Reserved", "Completed"]);

    const additionalClause = (code) => {
      if (adding || (call.stage && call.stage.order < 5)) {
        return validStatuses.has(code.label);
      }
      if (call.stage && call.stage.order < 5) {
        return validStatuses.has(code.label);
      }
      return true;
    };

    const options = codes
      .filter(
        (code) =>
          code.entity === "Call" &&
          code.field === "status" &&
          additionalClause(code)
      )
      .map((code) => ({
        value: code._id,
        label: code.label,
        order: code.order
      }));

    return sortBy(options, "label");
  }, [adding, call, codes]);

  const locationOptions = useMemo(() => {
    let options = [];
    if (state.customer && state.customer.locations) {
      options = state.customer.locations.map((location) => ({
        value: location._id,
        label: location.name
      }));
    }

    return sortBy(options, "label");
  }, [state.customer]);

  const callTypeOptions = useMemo(() => {
    const options = codes
      .filter((code) => code.entity === "Call" && code.field === "type")
      .map((code) => ({
        value: code._id,
        label: code.label,
        order: code.order
      }));

    return sortBy(options, "order");
  }, [codes]);

  // Gravoc customer and Personal Time project - only show personal services
  // Gravoc customer and not personal time project, show all services that are not personal
  // For all other customers, filters by services of the users department and ones for all departments
  const serviceOptions = useMemo(() => {
    const personalServices = new Set([961, 985, 991, 992, 993, 994]);
    const options = services
      .filter((service) => {
        if (!adding || service.status) {
          const department = state.assigned_to?.department
            ? state.assigned_to.department._id
            : auth.user.department._id;
          if (state.customer && state.customer.number !== "GRAVOCAS01") {
            if (
              (service.department && service.department._id === department) ||
              service.all_departments === true
            ) {
              return service;
            }
          } else if (
            state.project &&
            state.project.project_number === "GRAVOCASSO-0076"
          ) {
            if (personalServices.has(service.code)) {
              return service;
            }
          } else if (!personalServices.has(service.code)) return service;
          return false;
        }
        return false;
      })
      .map((service) => ({
        value: service._id,
        label: service.description
      }));

    return sortBy(options, "label");
  }, [
    adding,
    auth,
    services,
    state.assigned_to,
    state.customer,
    state.project
  ]);

  const assignedToOptions = useMemo(() => {
    let options = [];
    if (auth.maxRole <= 2000) {
      // Employee - show only logged in user's calls
      options = users.filter((user) => user._id === auth.user._id);
    } else if (auth.maxRole > 2000) {
      options = users;
    }

    return sortBy(
      options.map((user) => ({
        value: user._id,
        label: user.full_name
      })),
      "label"
    );
  }, [auth, users]);

  const isFormDisabled = useMemo(
    () =>
      call.stage &&
      call.stage.order > 4 &&
      !auth.user.roles.map((role) => role.name).includes("Billing Admin"),
    [call, auth]
  );

  const isRecurringDisabled = (arrival_date, completed_date) => {
    const hours = moment(completed_date).diff(arrival_date, "hours");
    if (isFormDisabled || hours > 24) {
      return true;
    }
    if (adding) {
      return !arrival_date && !completed_date;
    }
    if (!adding && editing) {
      return !arrival_date && !completed_date;
    }
    return true;
  };

  const calculateDuration = (startTime, newState) => {
    const start = startTime || newState.arrival_date || call.arrival_date;
    if (start && newState.tasks.length) {
      const tasksWithMinutes = newState.tasks.map(
        ({ actual_duration, duration_units }) =>
          (duration_units.value || duration_units) === "Minutes"
            ? actual_duration / 60
            : actual_duration
      );
      const tasksHours = tasksWithMinutes.reduce(
        (total, task) => Number(total) + Number(parseFloat(task) || 0)
      );

      return {
        actual_hrs: Number(tasksHours).toFixed(2),
        completed_date: moment(start).add(tasksHours, "hours")
      };
    }
  };

  const onChangeDate = (date, name) => {
    setIsFormInputsChanged(true);
    setState((prev) => {
      const newState = {
        ...prev,
        [name]: date
      };
      const durationInfo = calculateDuration(date, newState);
      return {
        ...newState,
        ...durationInfo
      };
    });
    setBlurTriggered((prev) => ({ ...prev, hasChanges: true }));
  };

  // if call type is_billable, check contract to determine billable_hrs
  const calculateBillableHours = (contractId, actual_hrs) => {
    const contract = contracts?.find((c) => c._id === contractId);
    const contractType = contract?.contract_type?.value;
    let billable_hrs = 0;
    switch (contractType) {
      case "FIXED FEE":
      case "RETAINER":
      case "CLIENTCARE":
      case "CAPITAL":
      case "MANAGED SERVICES":
        billable_hrs = actual_hrs;
        break;
      default:
        billable_hrs = Math.ceil(actual_hrs);
        break;
    }
    return billable_hrs;
  };

  const onChange = (field, input) => {
    setIsFormInputsChanged(true);
    const originalCallType = codes.find(
      (code) => code.entity === "Call" && code.value === call?.call_type?.value
    );
    const stateCallType = codes.find(
      (code) => code.entity === "Call" && code._id === state.call_type
    );

    setBlurTriggered((prev) => ({ ...prev, hasChanges: true }));
    switch (field) {
      case "tasks":
        setState((prev) => {
          const newState = {
            ...prev,
            tasks: input
          };
          const durationInfo = calculateDuration(null, newState);
          return {
            ...newState,
            ...durationInfo
          };
        });
        break;
      case "customer":
        setState((prev) => ({
          ...prev,
          customer: customers?.find((cust) => cust._id === input.value),
          contract: {},
          project: {}
        }));
        break;
      case "service":
        setState((prev) => ({
          ...prev,
          service: input.value,
          tasks: prev.tasks.map((task) => ({
            ...task,
            service: input.value,
            service_task: ""
          }))
        }));
        break;
      case "project": {
        const currentProject = projects?.find(
          (proj) => proj._id === input.value
        );
        let contract = {};
        if (currentProject.contract) {
          contract = contracts?.find(
            (cont) => cont._id === currentProject.contract._id
          );
        }
        const updated = {
          project: currentProject,
          contract
        };
        // update call type and billable hours if needed
        const call_type = codes?.find(
          (code) =>
            code.entity === "Call" &&
            code.value === currentProject.call_type.value
        );
        if (
          stateCallType?._id !== call_type._id ||
          originalCallType?._id !== call_type._id
        ) {
          updated.call_type = call_type._id;
          // if call has been aproved by a manager, notify of billable hours conflict, otherwise, update
          if (call.stage?.order >= 5) {
            if (originalCallType.is_billable !== call_type.is_billable) {
              alert(
                "Note: This change conflicts with the number of billable hours."
              );
            }
          } else if (call_type.is_billable) {
            updated.billable_hrs =
              calculateBillableHours(call.contract, call.actual_hrs) || 0;
          } else {
            updated.billable_hrs = 0;
          }
        }

        setState((prev) => ({
          ...prev,
          ...updated
        }));
        break;
      }
      // update project and contract
      case "assigned_to": {
        const assigned_to = users.find((u) => u._id === input.value);
        setState((prev) => ({ ...prev, assigned_to }));
        break;
      }
      case "workplace":
        setState((prev) => ({
          ...prev,
          workplace: codes?.find((code) => code.id === input.value)
        }));
        break;
      case "priority":
        setState((prev) => ({
          ...prev,
          priority: codes?.find((code) => code.id === input.value)
        }));
        break;
      case "stage": {
        const updates = {
          stage: codes?.find((code) => code.id === input.value)
        };
        setState((prev) => ({
          ...prev,
          ...updates
        }));
        break;
      }
      default:
        setState((prev) => ({ ...prev, [field]: input.value }));
        break;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setBlurTriggered((prev) => ({ ...prev, hasChanges: false }));
    const model = cloneDeep(state);
    delete model.blurTriggered;
    delete model.focusedInput;
    delete model.hasChanges;
    model.contract = state.project.contract ? state.project.contract._id : null;
    model.entry_date = new Date();
    model.department = state.assigned_to.department._id;
    model.priority = state.priority?._id || null;
    model.assigned_to = state.assigned_to?._id;
    model.stage = state.stage?._id;
    model.customer = state.customer?._id;
    model.project = state.project?._id;
    model.workplace = state.workplace?._id;

    // sanitize the textarea inputs of html tags, causes slight issue in keeping the undefined value and making it a string, handled below
    model.internal_notes = stripHtml(model.internal_notes);
    model.billing_description = stripHtml(model.billing_description);

    if (
      model.billing_description === "" ||
      model.billing_description === undefined ||
      model.billing_description === "undefined"
    ) {
      model.billing_description = "";
    }

    const callTypes = codes.filter(
      (c) => c.entity === "Call" && c.field === "type"
    );

    const type = callTypes.find((c) => c._id === model.call_type);

    if (type && type.is_billable) {
      model.billable_hrs =
        calculateBillableHours(model.contract, model.actual_hrs) || 0;
    } else {
      model.billable_hrs = 0;
    }

    if (state?.recurrence?.frequency) {
      onAddWithRecurring(model, state.recurrence);
    } else {
      onAdd(model);
    }
  };

  const prepForSaving = (updateBillableHours) => {
    setIsFormInputsChanged(false);
    try {
      const model = cloneDeep(state);
      delete model.blurTriggered;
      delete model.focusedInput;
      delete model.hasChanges;
      const nullServiceTask = state.tasks.find((task) => !task.service_task);
      if (nullServiceTask) {
        throw new Error("All fields on tasks are required");
      } else if (!state.tasks.length) {
        throw new Error("Please add some tasks");
      }
      setBlurTriggered((prev) => ({
        ...prev,
        hasChanges: false
      }));

      model.tasks = state.tasks.map(
        ({
          service_task,
          actual_duration,
          service,
          duration_units,
          external_pm_number
        }) => ({
          service_task: service_task.value || service_task,
          duration_units: duration_units.value || duration_units,
          actual_duration,
          service: service || state.service,
          external_pm_number
        })
      );
      model.contract = state.project.contract
        ? state.project.contract._id
        : null;
      model.entry_date = new Date();
      model.arrival_date = state.arrival_date;
      model.completed_date = state.completed_date;
      model.department = state.assigned_to.department._id;
      model.priority = state.priority?._id || null;
      model.stage = state.stage?._id;

      // sanitize the textarea inputs of html tags
      model.internal_notes = stripHtml(model.internal_notes);
      model.billing_description = stripHtml(model.billing_description);

      // the sanitize will take an undefined value and bring back 'undefined' which is a valid string, this fixes that issue
      if (
        model.billing_description === "" ||
        model.billing_description === undefined ||
        model.billing_description === "undefined"
      ) {
        model.billing_description = "";
      }

      const callTypes = codes.filter(
        (c) => c.entity === "Call" && c.field === "type"
      );

      const type = callTypes.find((c) => c._id === model.call_type);

      if (updateBillableHours) {
        if (type && type.is_billable) {
          model.billable_hrs =
            calculateBillableHours(model.contract, model.actual_hrs) || 0;
        } else {
          model.billable_hrs = 0;
        }
      }

      if (model.invoiced_date) {
        model.invoiced_date = moment(model.invoiced_date).toDate();
      }
      if (model.cancel_date) {
        model.cancel_date = moment(model.cancel_date).toDate();
      }

      if (
        state?.days_of_week?._id ||
        state?.recurrence?.dates?.length ||
        recurring
      ) {
        onSaveWithRecurring(model, state.recurrence);
      } else {
        onSave(model);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getNewBillableHours = () => {
    const callTypes = codes.filter(
      (c) => c.entity === "Call" && c.field === "type"
    );

    const type = callTypes.find((c) => c._id === state.call_type);

    return type && type.is_billable
      ? calculateBillableHours(state.contract?._id, state.actual_hrs) || 0
      : 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (
      (state.stage.label === "Submitted" ||
        state.stage.label === "Ready to Invoice" ||
        state.stage.label === "Invoiced" ||
        state.stage.label === "Billed" ||
        state.stage.label === "Cancelled" ||
        state.stage.label === "Imported") &&
      getNewBillableHours() !== state.billable_hrs
    ) {
      actions.showModalSuccess("confirmUpdateCall");
    } else {
      prepForSaving(true);
    }
  };

  const handleSaveRecurrence = (model, dates, summary) => {
    const recurrenceInfo = { ...model, dates, summary };
    setState((prev) => ({
      ...prev,
      recurrence: recurrenceInfo,
      hasChanges: true
    }));
    actions.hideModal();
  };

  const cancel = async () => {
    setBlurTriggered((prev) => ({ ...prev, hasChanges: false }));
    onCancel();
  };

  const handleClickRecurring = () => {
    actions.getRecurrence(call.recurring_id, true);
  };

  const getDateErrorMessage = () => {
    const { arrival_date, completed_date } = state;
    if (
      arrival_date instanceof moment &&
      completed_date instanceof moment &&
      completed_date.isBefore(arrival_date)
    ) {
      return "End Time Must Be After Start Time";
    }
    if (
      arrival_date instanceof moment &&
      completed_date instanceof moment &&
      completed_date.clone().subtract(24, "h").isAfter(arrival_date)
    ) {
      return "Call Time Can Not Exceed 24 hours";
    }
    return "";
  };

  const confirmUpdateCallModal = (
    <ConfirmUpdateCallModal
      call={{
        stage: state.stage._id,
        customer: state.customer && state.customer._id,
        project: state.project && state.project._id,
        call_type: state.call_type,
        service_note: state.contract && state.contract.service_note,
        assigned_to: state.assigned_to ? state.assigned_to._id : auth.user._id,
        arrival_date: state.arrival_date || call.arrival_date,
        completed_date: state.completed_date || call.completed_date,
        cancel_by: state.cancel_by || call.cancel_by,
        actual_hrs: state.actual_hrs || call.actual_hrs || 0,
        billable_hrs: state.billable_hrs || call.billable_hrs,
        cancel_date: moment(call.cancel_date) || "",
        cancel_reason: call.cancel_reason || "",
        workplace: state.workplace._id,
        priority: state.priority?._id || call.priority || null,
        location: state.location || call.location,
        internal_notes: call.internal_notes,
        billing_description: call.billing_description,
        project_requestor: state.project_requestor || call.project_requestor,
        off_hours: call.off_hours,
        fixed_time: call.fixed_time
      }}
      codes={codes}
      onCancel={actions.hideModal}
      onSubmit={(c, v) => prepForSaving(v)}
    />
  );
  const recurringCallForm = (
    <RecurringCallForm
      recurrence={state.recurrence}
      call={call}
      codes={codes}
      contract={state.contract}
      adding={adding}
      editing={editing}
      start_date={state.arrival_date || call.arrival_date}
      actual_hrs={state.actual_hrs || call.actual_hrs}
      onCancel={actions.hideModal}
      onSubmit={handleSaveRecurrence}
    />
  );
  return (
    <React.Fragment>
      <form
        className="vertical form"
        disabled={isFormDisabled}
        onSubmit={adding ? add : submit}
      >
        <Prompt
          when={!!blurTriggered?.hasChanges && !saving}
          message={() =>
            `Are you sure you want to leave this page? Changes you made may not be saved`
          }
        />
        <Row>
          <Col md={12}>
            <fieldset>
              <legend className="flex -space-between">
                Call Info
                <span style={{ margin: 0, float: "right" }}>
                  <Badge className={getClassName(state.priority?.label)}>
                    {state.priority?.label}
                  </Badge>
                  Entry Date:{" "}
                  {formatDate(call.created_on, "MM/dd/yyyy hh:mm a")}
                </span>
              </legend>
            </fieldset>
          </Col>
          <Col xs={12}>
            <Alert
              show={
                customer?.bc_blocked === "All" ||
                state.customer?.bc_blocked === "All"
              }
            >
              <p>
                This customer’s account has been blocked in Business Central.
                Please check with your manager.
              </p>
            </Alert>
          </Col>
          {state.project && state.project._id ? (
            <Col xs={12} style={{ padding: "10px 30px" }}>
              <TimesheetProgressBar
                projectId={state.project._id}
                call={{
                  ...call,
                  arrival_date: state.arrival_date || call.arrival_date
                }}
                tasks={state.tasks}
                dates={[]}
                auth={auth}
                clearProjectOnUnmount={clearProgressBarOnUnmount}
              />
            </Col>
          ) : null}
          <Col md={4} xs={12}>
            <DropdownSearchWithNoFormsy
              required
              name="customer"
              label="Customer"
              onChange={onChange}
              disabled={!editing || customer || isFormDisabled}
              value={state.customer && state.customer._id}
              data={[
                ...sortBy(
                  customers.map((cust) => ({
                    value: cust._id,
                    label: cust.name
                  })),
                  [(option) => option.label.toLowerCase()]
                )
              ]}
            />
          </Col>
          <Col md={4} xs={12}>
            <DropdownSearchWithNoFormsy
              required
              name="project"
              label="Project"
              onChange={onChange}
              disabled={!editing || isFormDisabled || project}
              data={[...projectOptions]}
              value={state.project && state.project._id}
            />
          </Col>
          <Col md={4} xs={12}>
            <DropdownSearchWithNoFormsy
              disabled
              required
              name="call_type"
              label="Call Type"
              data={[...callTypeOptions]}
              value={state.call_type}
            />
          </Col>
          {state.contract && state.contract.service_note && (
            <Col md={12} xs={12}>
              <CustomTextarea
                name="service_note"
                label="Service Note"
                rows={3}
                value={state.contract.service_note}
                readOnly
                disabled={!editing || isFormDisabled}
              />
            </Col>
          )}
          <Col md={4} xs={12}>
            <DropdownSearchWithNoFormsy
              required
              name="stage"
              label="Status"
              disabled={!editing || isFormDisabled}
              onChange={onChange}
              data={stageOptions}
              value={state.stage._id}
            />
          </Col>
          <Col md={4} xs={12}>
            <DropdownSearchWithNoFormsy
              name="assigned_to"
              label="Assigned To"
              onChange={onChange}
              disabled={!editing || isFormDisabled}
              data={[...assignedToOptions]}
              value={state.assigned_to ? state.assigned_to._id : auth.user._id}
            />
          </Col>
          <Col md={4} xs={12}>
            <DropdownSearchWithNoFormsy
              required
              name="service"
              label="Service"
              onChange={onChange}
              disabled={!editing || isFormDisabled}
              data={[...serviceOptions]}
              value={state.service}
            />
          </Col>
          {state.service && (
            <>
              <Col xs={1} />
              <Col xs={10}>
                <TasksServiceContainer
                  call={state}
                  serviceId={
                    state.service.length ? state.service : state.service._id
                  }
                  disabled={!editing || isFormDisabled}
                  tasks={state.tasks}
                  onChange={onChange}
                  adding={adding}
                />
              </Col>
              <Col xs={1} />
            </>
          )}
          <Col md={4} xs={12}>
            <DateTimePickerWithNoFormsy
              required
              name="arrival_date"
              label="Start Time"
              onChange={onChangeDate}
              disabled={!editing || isFormDisabled}
              value={state.arrival_date || call.arrival_date}
            />
          </Col>
          <Col md={4} xs={12}>
            <DateTimePickerWithNoFormsy
              disabled
              name="completed_date"
              label="End Time"
              value={state.completed_date || call.completed_date}
              getErrorMessage={getDateErrorMessage}
            />
          </Col>
          <Col md={2} xs={2} style={{ marginTop: 10 }}>
            <CustomNumberInput
              required={editing && true}
              formNoValidate
              type="number"
              name="actual_hrs"
              label="Actual Hours"
              onChange={debounce(onChange, 500)}
              disabled
              value={String(state.actual_hrs || call.actual_hrs || 0)}
              step="0.01"
              pattern="[0-9]*"
            />
          </Col>
          {state.managerApproved && (
            <Col md={2} xs={12} style={{ marginTop: 10 }}>
              <CustomNumberInput
                name="billable_hrs"
                label="Billable Hours"
                pattern="[0-9]*"
                disabled={!editing || state.managerApproved}
                value={String(state.billable_hrs || call.billable_hrs)}
              />
            </Col>
          )}
          <Col
            md={2}
            xs={12}
            style={{
              display: state.managerApproved ? "none" : "inherit"
            }}
          >
            <div
              className="form-group row"
              data-tip={
                editing &&
                !isFormDisabled &&
                !(state.arrival_date || call.arrival_date)
                  ? `<div>Enter a start time to create recurrence</div>`
                  : ``
              }
              data-html
              data-for="info"
              id="recurrence"
              data-tooltip-content="Enter a start time to create recurrence"
              data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
            >
              <label htmlFor="form" style={{ visibility: "hidden" }}>
                *
              </label>
              <button
                className="btn btn-inline btn-blue"
                type="button"
                onClick={handleClickRecurring}
                disabled={isRecurringDisabled(
                  state.arrival_date || call.arrival_date,
                  state.completed_date || call.completed_date
                )}
              >
                Recurrence
              </button>
            </div>
            <ReactTooltip
              anchorId="recurrence"
              className={`custom-tooltip ${
                auth.isNightMode ? "dark" : "light"
              }`}
              id="info"
              place="left"
              type={auth.isNightMode ? "dark" : "light"}
              effect="solid"
              globalEventOff="click"
            />
          </Col>
          {!isEmpty(state.recurrence) && (
            <Col md={12} xs={12}>
              <h5>{state.recurrence.summary}</h5>
            </Col>
          )}
          {state.stage?.label === "Cancelled" && (
            <React.Fragment>
              <Col md={4} xs={12}>
                <DropdownSearchWithNoFormsy
                  name="cancel_by"
                  label="Cancelled By"
                  value={state.cancel_by || call.cancel_by}
                  onChange={onChange}
                  disabled={!editing}
                  data={[
                    ...users.map((user) => ({
                      value: user._id,
                      label: user.full_name
                    }))
                  ]}
                />
              </Col>
              <Col md={4} xs={12}>
                <DateTimePickerWithNoFormsy
                  name="cancel_date"
                  label="Cancel Date"
                  value={moment(call.cancel_date) || ""}
                  disabled={!editing}
                  showTimeSelect={false}
                  onChange={onChangeDate}
                />
              </Col>
              <Col md={4} xs={12} style={{ marginTop: 10 }}>
                <CustomInput
                  formNoValidate
                  onChange={(n, v) => onChange(n, { value: v })}
                  name="cancel_reason"
                  label="Cancel Reason"
                  value={call.cancel_reason || ""}
                  disabled={!editing}
                />
              </Col>
            </React.Fragment>
          )}
          <Col
            md={state.workplace && state.workplace.label === "Onsite" ? 4 : 6}
            xs={12}
          >
            <DropdownSearchWithNoFormsy
              name="priority"
              label="Priority"
              disabled={!editing || isFormDisabled}
              data={[
                ...codes
                  .filter(
                    (code) =>
                      code.entity === "Call" && code.field === "priority"
                  )
                  .map((code) => ({
                    value: code._id,
                    label: code.label
                  }))
              ]}
              onChange={onChange}
              value={state.priority?._id}
            />
          </Col>
          <Col
            md={state.workplace && state.workplace.label === "Onsite" ? 4 : 6}
            xs={12}
          >
            <DropdownSearchWithNoFormsy
              required
              name="workplace"
              label="Workplace"
              disabled={!editing || isFormDisabled}
              data={[
                ...codes
                  .filter((code) => code.field === "workplace")
                  .map((code) => ({
                    value: code._id,
                    label: code.label
                  }))
              ]}
              onChange={onChange}
              value={state.workplace._id}
            />
          </Col>
          {state.workplace && state.workplace.label === "Onsite" && (
            <Col xs={4}>
              <DropdownSearchWithNoFormsy
                name="location"
                label="Location"
                disabled={!editing || isFormDisabled}
                data={[...locationOptions]}
                onChange={onChange}
                value={state.location || call.location}
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col md={6} xs={12}>
            <CustomTextarea
              required
              formNoValidate
              type="text"
              name="internal_notes"
              label="Internal Notes"
              value={call.internal_notes}
              onChange={(n, v) => {
                onChange(n, { value: v });
              }}
              disabled={!editing || isFormDisabled}
              validations={{ maxLength: 500 }}
              validationErrors={{
                maxLength: "You can not type in more than 500 characters"
              }}
            />
          </Col>
          <Col md={6} xs={12}>
            <CustomTextarea
              formNoValidate
              type="text"
              name="billing_description"
              label="Customer Billing Notes"
              value={call.billing_description}
              onChange={(n, v) => {
                onChange(n, { value: v });
              }}
              disabled={!editing || isFormDisabled}
              validations={{ maxLength: 100 }}
              validationErrors={{
                maxLength: "You can not type in more than 100 characters"
              }}
            />
          </Col>
          {((project && project.requestor_required) ||
            (call && call.project_requestor)) && (
            <Col md={6} xs={12}>
              <CustomInput
                onChange={(_, v) => onChange("project_requestor", { value: v })}
                required={project && project.requestor_required}
                name="project_requestor"
                label="Project Requestor"
                disabled={!editing || isFormDisabled}
                value={state.project_requestor || call.project_requestor}
              />
            </Col>
          )}
          <Col md={2} xs={6}>
            <Checkbox
              checked={state.off_hours}
              formNoValidate
              name="off_hours"
              label="Off Hours"
              value={state.off_hours}
              disabled={!editing}
              onClickCheck={(e) => {
                onChange("off_hours", { value: e.target?.checked });
              }}
            />
          </Col>
          <Col md={2} xs={6}>
            <Checkbox
              checked={state.fixed_time}
              formNoValidate
              name="fixed_time"
              label="Fixed Time"
              value={state.fixed_time}
              disabled={!editing}
              onClickCheck={(e) => {
                onChange("fixed_time", { value: e.target?.checked });
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12} xs={12} className="footer">
            <button className="btn  btn-cancel" type="button" onClick={cancel}>
              Cancel
            </button>

            {!isFormDisabled && !readOnly && (
              <input
                className="btn btn-success"
                type="submit"
                disabled={
                  !canSubmit ||
                  saving ||
                  getDateErrorMessage() ||
                  !isFormInputsChanged
                }
                value={saving ? "Saving... " : "Save"}
              />
            )}
          </Col>
        </Row>
      </form>
      <Modal
        id="confirmUpdateCall"
        title="Saving this call will override the billable hours. Would you like to save the existing value or update the billable hours?"
        body={confirmUpdateCallModal}
        close={actions.hideModal}
      />
      <Modal
        id="recurringCallsModal"
        title="Schedule Recurring"
        body={recurringCallForm}
        close={actions.hideModal}
      />
    </React.Fragment>
  );
};

CallForm.propTypes = {
  call: PropTypes.object,
  codes: PropTypes.array,
  customers: PropTypes.array,
  contracts: PropTypes.array,
  projects: PropTypes.array,
  users: PropTypes.array,
  recurrence: PropTypes.object,
  adding: PropTypes.bool,
  auth: PropTypes.object,
  customer: PropTypes.object,
  project: PropTypes.object,
  onAddWithRecurring: PropTypes.func,
  onAdd: PropTypes.func,
  onSave: PropTypes.func,
  onSaveWithRecurring: PropTypes.func,
  recurring: PropTypes.bool,
  actions: PropTypes.object,
  onCancel: PropTypes.func,
  services: PropTypes.array,
  editing: PropTypes.bool,
  saving: PropTypes.bool,
  readOnly: PropTypes.bool,
  clearProgressBarOnUnmount: PropTypes.bool
};

CallForm.defaultProps = {
  readOnly: false
};
function mapStatesToProps(state) {
  return {
    state: state.reducers,
    recurrence: state.reducers.recurrence
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...modalActions, ...callActions }, dispatch)
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(CallForm);
